import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ExternalLink, HardHat, Home, ListChecks, ShieldCheck, UserCog, Wrench, type LucideIcon } from 'lucide-react'
import { cn, MobileNavigation, SheetClose } from '@asad/crane-ui'

import craneoLogo from '@/assets/img/fud-finance-logo.png'
import { getFallbackPath } from '@/components/router/protected-route'
import { useAppSelector } from '@/hooks/reduxHooks'

import { SHOP_LINK } from '../navigation'

/** Pasek głównych modułów systemu, zakończony linkiem do sklepu. */

type RailItem = {
  to: string
  labelKey: string
  shortKey?: string
  Icon: LucideIcon
  roles: string[]
  match: (pathname: string) => boolean
}

const ITEMS: RailItem[] = [
  {
    to: '/dashboard',
    labelKey: 'nav.rail.monitoring',
    Icon: Home,
    roles: ['ADMIN', 'USER'],
    match: (p) => p === '/' || p === '/dashboard' || p.startsWith('/device/'),
  },
  {
    to: '/service',
    labelKey: 'nav.rail.service',
    Icon: Wrench,
    roles: ['SERVICE_MANAGER'],
    match: (p) => p.startsWith('/service'),
  },
  {
    to: '/my-orders',
    labelKey: 'service.myOrders',
    shortKey: 'nav.rail.myOrders',
    Icon: HardHat,
    roles: ['SERVICE_TECHNICIAN'],
    match: (p) => p.startsWith('/my-orders'),
  },
  {
    to: '/admin',
    labelKey: 'common.administration',
    shortKey: 'nav.rail.admin',
    Icon: UserCog,
    roles: ['ADMIN'],
    match: (p) => p.startsWith('/admin'),
  },
  {
    to: '/rodo',
    labelKey: 'rodo.title',
    shortKey: 'nav.rail.rodo',
    Icon: ShieldCheck,
    roles: ['ADMIN_RODO'],
    match: (p) => p.startsWith('/rodo'),
  },
  {
    to: '/audit-log',
    labelKey: 'auditLog.menuTitle',
    shortKey: 'nav.rail.auditLog',
    Icon: ListChecks,
    roles: ['ADMIN_AUDIT_LOG'],
    match: (p) => p.startsWith('/audit-log'),
  },
]

type SidebarProps = { mobile?: boolean }

const Sidebar = ({ mobile = false }: SidebarProps) => {
  const { pathname, search, key } = useLocation()
  const { t } = useTranslation()
  const profile = useAppSelector((state) => state.profile?.data)

  const userRoles: string[] = profile?.role ?? []
  const items = ITEMS.filter((item) => item.roles.some((role) => userRoles.includes(role)))
  const shopHint = t(SHOP_LINK.descriptionKey)

  if (mobile)
    return (
      <MobileNavigation
        title={t('menu.mainNavigation')}
        closeLabel={t('commonActions.close')}
        navigationKey={key + pathname + search}
      >
        {items.map(({ to, labelKey, Icon, match }) => (
          <SheetClose key={to} asChild>
            <Link
              to={to}
              aria-current={match(pathname) ? 'page' : undefined}
              className={cn(
                'flex min-h-11 shrink-0 items-center gap-3 rounded-crane-md px-3 py-2 text-sm font-medium text-crane-foreground hover:bg-crane-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-crane-primary',
                match(pathname) && 'bg-crane-primary/10 text-crane-primary',
              )}
            >
              <Icon className='size-5 shrink-0' aria-hidden />
              <span className='min-w-0 break-words'>{t(labelKey)}</span>
            </Link>
          </SheetClose>
        ))}
        <SheetClose asChild>
          <a
            href={SHOP_LINK.url}
            target='_blank'
            rel='noopener noreferrer'
            className='mt-auto flex min-h-11 shrink-0 items-center gap-3 rounded-crane-md px-3 py-2 text-sm text-crane-muted-foreground hover:bg-crane-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-crane-primary'
          >
            <ExternalLink className='size-5 shrink-0' aria-hidden />
            <span>
              {SHOP_LINK.label} · {shopHint}
            </span>
          </a>
        </SheetClose>
      </MobileNavigation>
    )

  return (
    <aside className='crane-ui-surface sticky top-0 z-50 hidden h-screen w-crane-sidebar-compact shrink-0 flex-col items-center bg-crane-sidebar py-2.5 md:flex md:w-crane-sidebar'>
      {/* Sygnet CRANEO wykadrowany z pełnego logo — ujemne offsety i szerokość 40 są celowe. */}
      <Link
        to={getFallbackPath(userRoles)}
        title='Craneo Data System'
        aria-label='Craneo Data System'
        className={cn(
          'relative mb-5 mt-1.5 h-6 w-crane-logo shrink-0 scale-75 overflow-hidden rounded-crane-sm opacity-90 transition md:scale-100',
          'hover:-translate-y-px hover:opacity-100',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-primary/40',
        )}
      >
        <img
          src={craneoLogo}
          alt='Craneo Data System'
          className='pointer-events-none absolute -left-4 -top-4 block h-auto w-40 max-w-none'
        />
      </Link>

      <nav className='flex w-full flex-1 flex-col items-center gap-2.5 overflow-y-auto crane-scrollbar-none md:gap-1.5'>
        {items.map(({ to, labelKey, shortKey, Icon, match }) => {
          const active = match(pathname)
          const itemLabel = t(labelKey)

          return (
            <NavLink
              key={to}
              to={to}
              title={itemLabel}
              className={cn(
                'relative flex size-crane-rail-item-compact shrink-0 flex-col items-center justify-center gap-crane-rail-gap rounded-crane-sm transition-colors md:h-crane-rail-item md:w-crane-rail-item',
                'text-crane-sidebar-foreground hover:bg-crane-sidebar-active/5 hover:text-crane-sidebar-active',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-crane-primary',
                active &&
                  'bg-crane-sidebar-active/6 text-crane-sidebar-active ring-1 ring-inset ring-crane-sidebar-active/10',
              )}
            >
              {active && (
                <span
                  aria-hidden
                  className='absolute -left-1 bottom-3.5 top-3.5 w-0.5 rounded-r-crane-sm bg-crane-primary opacity-90'
                />
              )}
              <Icon className='size-crane-rail-icon' strokeWidth={1.6} aria-hidden />
              <span className='hidden max-w-12 whitespace-nowrap text-center text-crane-rail-label tracking-crane-rail md:block'>
                {shortKey ? t(shortKey) : itemLabel}
              </span>
            </NavLink>
          )
        })}
      </nav>

      <a
        href={SHOP_LINK.url}
        target='_blank'
        rel='noopener noreferrer'
        title={`${SHOP_LINK.label} — ${shopHint}`}
        className={cn(
          'mt-2 flex w-crane-rail-item-compact shrink-0 flex-col items-center gap-0.5 rounded-crane-sm px-1 pb-1 pt-2 md:w-crane-rail-item',
          'border-t border-solid border-crane-sidebar-active/10 text-crane-sidebar-foreground/55 transition-colors',
          'hover:bg-crane-sidebar-active/5 hover:text-crane-sidebar-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-crane-primary',
        )}
      >
        <ExternalLink className='size-3.5' strokeWidth={1.6} aria-hidden />
        <span className='hidden whitespace-nowrap text-crane-rail-shop font-semibold md:block'>{SHOP_LINK.label}</span>
        <span className='hidden whitespace-nowrap text-crane-rail-hint opacity-80 md:block'>{shopHint}</span>
      </a>
    </aside>
  )
}

export { Sidebar }
export default Sidebar
